"use client";

import { useState } from "react";
import { plainText } from "@/lib/assistant-format";
import { streamAssistant } from "@/lib/assistant-stream";
import { assistantSessionId } from "@/lib/assistant-session";
import LinkifiedText from "@/app/components/LinkifiedText";

export interface AssistantChannel {
  label: string;
  href: string;
}

/**
 * Помощь с запросом в компанию: ассистент объясняет, что компания умеет, и
 * готовит черновик обращения. Черновик можно скопировать и отправить в один
 * из каналов компании; если писать самому не хочется — рядом CTA к Dev48.
 */
export default function CompanyRequestAssistant({
  slug,
  companyName,
  locale,
  channels,
}: {
  slug: string;
  companyName: string;
  locale: string;
  channels: AssistantChannel[];
}) {
  const isEn = locale.startsWith("en");
  const [task, setTask] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const ask = async () => {
    setLoading(true);
    setError("");
    setCopied(false);
    try {
      const result = await streamAssistant(
        {
          scene: "company-request",
          slug,
          message: task,
          locale,
          sessionId: assistantSessionId(),
        },
        (chunk) => setReply((prev) => prev + chunk)
      );
      setReply(plainText(result.reply));
    } catch {
      setError(isEn ? "Could not reach the assistant" : "Не удалось получить ответ");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reply);
      setCopied(true);
    } catch {
      setError(isEn ? "Copy failed" : "Не удалось скопировать");
    }
  };

  return (
    <section className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
      <h2 className="text-xl font-semibold text-white">
        {isEn ? `Need to contact ${companyName}?` : `Нужно обратиться в ${companyName}?`}
      </h2>
      <p className="mt-1 text-sm text-white/60">
        {isEn
          ? "Describe the task — the assistant will explain what the company does and draft a request you can send."
          : "Опишите задачу — ассистент подскажет, что умеет компания, и подготовит текст запроса."}
      </p>

      <textarea
        value={task}
        onChange={(event) => setTask(event.target.value)}
        rows={3}
        placeholder={
          isEn
            ? "For example: need a mobile app for food delivery, launch in 4 months"
            : "Например: нужна мобильная разработка для доставки, запуск через 4 месяца"
        }
        className="mt-4 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-white/30 focus:outline-none"
      />

      <div className="mt-3 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void ask()}
          disabled={loading}
          className="inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#202e64] transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading
            ? isEn
              ? "Drafting…"
              : "Готовлю…"
            : isEn
              ? "Draft a request"
              : "Подготовить запрос"}
        </button>
        <a
          href={`/${locale}/book-call?source=company-assistant`}
          className="inline-flex items-center rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
        >
          {isEn ? "Let Dev48 help" : "Поможем подобрать"}
        </a>
      </div>

      {error && <p className="mt-3 text-sm text-red-300">{error}</p>}

      {reply && (
        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-white/85"><LinkifiedText text={reply} /></div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void copy()}
              className="rounded-full border border-white/20 px-3 py-1.5 text-xs text-white/70 transition-colors hover:border-white/40 hover:text-white"
            >
              {copied ? (isEn ? "Copied" : "Скопировано") : isEn ? "Copy" : "Скопировать"}
            </button>
            {channels.map((channel) => (
              <a
                key={channel.href}
                href={channel.href}
                target={channel.href.startsWith("http") ? "_blank" : undefined}
                rel={channel.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="text-xs text-blue-300 transition-colors hover:text-blue-200"
              >
                {isEn ? "Send via" : "Отправить в"} {channel.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
